import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';

type PromiseType = {
  loading: ?boolean,
  data: ?any,
  error: ?any
}

export const usePromise = (call, context) : PromiseType => {
  const [{ loading, data, error }, setState] = useState({ loading: true });
  useLayoutEffect(() => {
    let mounted = true;
    setState({ loading: true });
    call()
      .then(data => mounted && setState({ data }))
      .catch(error => mounted && setState({ error }));
    return () => { mounted = false; };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, context);

  return useMemo(() => ({ loading, data, error }), [data, error, loading]);
};

export const createLazySingletonContext = (initValue, useContextBody) => {
  let currentContextValue = initValue;
  const Context = createContext([currentContextValue, false, () => { console.error(`setHasConsumer called without matching provider. Did you forget to wrap App in Provider?`); }]);
  let setContextValue = () => { console.error('should not be called. Context update might be lost'); };

  const BodyExecutorNoop = () => null;

  const BodyExecutor = () => {
    currentContextValue = useContextBody();
    setContextValue(currentContextValue);
    return null;
  };

  const ProviderBody = ({ children, hasConsumer, setHasConsumer }) => {
    const [value, setValue] = useState(initValue);
    setContextValue = setValue;
    const hookContext = useMemo(() => [value, hasConsumer, setHasConsumer], [value, hasConsumer, setHasConsumer]);

    return (
      <Context.Provider value={hookContext}>
        {children}
      </Context.Provider>
    );
  };

  const Provider = ({ children }) => {
    const [hasConsumer, setHasConsumer] = useState(false);
    return (
      <>
        {hasConsumer && <BodyExecutor/>}
        {!hasConsumer && <BodyExecutorNoop/>}
        <ProviderBody hasConsumer={hasConsumer} setHasConsumer={setHasConsumer}>
          {children}
        </ProviderBody>
      </>
    );
  };

  const useHook = () => {
    let [value, hasConsumer, setHasConsumer] = useContext(Context);
    useLayoutEffect(() => { if (!hasConsumer) setHasConsumer(true); }, [hasConsumer, setHasConsumer]);
    return value;
  };

  const getCurrentValue = () => currentContextValue;

  return { Provider, useHook, getCurrentValue };
};
